import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContentHead,
  Form,
  FormCurrencyField,
  FormSearchableSelect,
  FormTextField,
  useForm
} from '../../lib/components';
import { ContentBox } from '../../lib/components/compound/pages/contentBox';
import { PageWithSidebar } from '../../lib/components/compound/pages/pageWithSidebar';
import { FormTaxClassSelect } from '../../lib/components/compound/forms/form/fields/taxFields/taxClassSelect';
import { FormChildAllowanceField } from '../../lib/components/compound/forms/form/fields/taxFields/childAllowanceField';
import { FormYesNoField } from '../../lib/components/compound/forms/form/fields/yesNoField';
import { Box, Grid, Typography } from '@mui/material';
import { useStore } from '../../hooks/useStore';
import { BenefitRow } from './benefitRow';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { FormikValues } from 'formik';
import { SalaryCard } from './salaryCard';
import { BenefitCard } from './benefitCard';
import { FormStateCard } from './formStateCard';
import { BenefitData, CompareCard } from './compareCard';
import { useBenefitOptions } from '../receipts/detailView/shared/useBenefitOptions';
import { BenefitKey } from '../../lib/components/compound/dataDisplay';
import { EmployeeBenefitSettings } from '@pxtra-client-lib-legacy/gen';
import Util from '../../misc/util';

export type CalculatorState = 'inital' | 'fine' | 'inconsistent' | 'loading' | 'error';

export const BenefitCalculator = observer(() => {
  const { t } = useTranslation();
  const { benefitsStore, employeeStore, companyStore } = useStore();
  const form = useForm();
  const [employeeList, setEmployeeList] = useState<{ value: string; text: string }[]>([]);
  const [state, setState] = useState<CalculatorState>('inital');
  const [benefitData, setBenefitData] = useState<BenefitData>();

  const preset: FormikValues = {
    employee: 'nobody',
    benefit_budget: 400,
    gross_salary: 3000,
    tax_class: 1,
    child_allowance: 0,
    church_tax: 0,
    birth_date: '1980-01-01',
    betreuungskosten_amount: 200,
    erholungsbeihilfe_amount: 156,
    essenszuschuss_amount: 103.5,
    fahrtkosten_amount: 10,
    internetpauschale_amount: 50,
    öpnv_tickets_amount: 49,
    telefonkosten_amount: 5,
    sachbezug_amount: 50
  };

  const max = useLocalObservable(() => ({
    values: {
      betreuungskosten: Infinity,
      erholungsbeihilfe: 156,
      essenszuschuss: 103.5,
      fahrtkosten: Infinity,
      internetpauschale: 50,
      öpnv_tickets: Infinity,
      telefonkosten: 20,
      sachbezug: 50,
      mpp: Infinity,
      jobbike: Infinity,
      bav: Infinity
    } as Record<BenefitKey, number>,
    updateErholungsbeihilfe(taxClass: number, childCount: number) {
      const newMaxValue = 156 + (taxClass >= 3 && taxClass <= 5 ? 104 : 0) + childCount * 52;
      const currentAmount = parseFloat(form.formRef.current?.values.erholungsbeihilfe_amount ?? 0);
      this.values.erholungsbeihilfe = newMaxValue;
      if (currentAmount > newMaxValue) {
        form.formRef.current?.setFieldValue('erholungsbeihilfe_amount', newMaxValue);
      }
    }
  }));

  const [formPreset, setFormPreset] = useState<FormikValues>(preset);

  const getEmployeeBenefitStatus = (benefit: keyof EmployeeBenefitSettings) => {
    return Util.isBenefitActive(benefit, employeeStore.currentEmployee?.benefit_settings);
  };

  const loadPresetByUser = async (id: string) => {
    await employeeStore.fetchOne(id);
    const employee = employeeStore.currentEmployee;
    const tax = employee?.tax_data;
    max.updateErholungsbeihilfe(
      typeof tax?.steuerklasse === 'number' ? (tax.steuerklasse as number) : 0,
      tax?.children_count ?? 0
    );
    setFormPreset(() => {
      return {
        ...preset,
        employee: id,
        birth_date: employee?.general_data.birth_date,
        benefit_budget: (employee?.benefit_budget?.total_available_amount ?? 0) / 100,
        tax_class: employee?.tax_data?.steuerklasse ?? 1,
        child_allowance: employee?.tax_data?.kinderfreibetrag ?? 0,
        church_tax: employee?.tax_data.religion ? 1 : 0,
        betreuungskosten_enabled: getEmployeeBenefitStatus('betreuungskosten'),
        erholungsbeihilfe_enabled: getEmployeeBenefitStatus('erholungsbeihilfe'),
        erholungsbeihilfe_amount: max.values.erholungsbeihilfe,
        essenszuschuss_enabled: getEmployeeBenefitStatus('essenszuschuss'),
        fahrtkosten_enabled: getEmployeeBenefitStatus('fahrtkosten'),
        internetpauschale_enabled: getEmployeeBenefitStatus('internetpauschale'),
        öpnv_tickets_enabled: getEmployeeBenefitStatus('öpnv_tickets'),
        telefonkosten_enabled: getEmployeeBenefitStatus('telefonkosten'),
        sachbezug_enabled: getEmployeeBenefitStatus('sachbezug')
      };
    });
  };

  useEffect(() => {
    setBenefitData({
      betreuungskosten: benefitsStore.calculation?.betreuungskosten ?? 0,
      essenszuschuss: benefitsStore.calculation?.essenszuschuss ?? 0,
      sachbezug: benefitsStore.calculation?.sachbezug ?? 0,
      bav: benefitsStore.calculation?.bav ?? 0,
      erholungsbeihilfe: benefitsStore.calculation?.erholungsbeihilfe ?? 0,
      fahrtkosten: benefitsStore.calculation?.fahrtkosten ?? 0,
      telefonkosten: benefitsStore.calculation?.telefonkosten ?? 0,
      internetpauschale: benefitsStore.calculation?.internetpauschale ?? 0,
      öpnv_tickets: benefitsStore.calculation?.öpnv_tickets ?? 0,
      jobbike: benefitsStore.calculation?.jobbike ?? 0,
      mpp: benefitsStore.calculation?.mpp ?? 0,
      bkv: benefitsStore.calculation?.bkv ?? 0
    });
  }, [benefitsStore.calculation]);

  const benefitList = useBenefitOptions({});

  useEffect(() => {
    let isCanceled = false;
    const fetch = async () => {
      if (!isCanceled) {
        const employees = await employeeStore.fetchList(['first_name', 'id', 'last_name']);
        const employeeList = [{ value: 'nobody', text: t('employee:NOBODY') }];

        setEmployeeList(
          employeeList.concat(
            employees?.map((e) => {
              return {
                value: e.id ?? '',
                text: `${e.first_name} ${e.last_name}`
              };
            }) ?? []
          )
        );
      }
    };
    fetch();
    return () => {
      isCanceled = true;
    };
  }, [companyStore.company]);

  return (
    <PageWithSidebar
      rightColumn={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'inherit', height: '100%' }}>
          <Box sx={{ display: 'flex', gap: 'inherit' }}>
            <SalaryCard
              salary={benefitsStore.calculation?.netto_with_benefits}
              reference={benefitsStore.calculation?.netto_without_benefits}
              sx={{ flex: 1 }}
            />
            <BenefitCard
              value={
                (benefitsStore.calculation?.netto_with_benefits ?? 0) -
                (benefitsStore.calculation?.netto_without_benefits ?? 0)
              }
              sx={{ flex: 1 }}
            />
          </Box>
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              height: '100%'
            }}
          >
            <CompareCard
              onResetClicked={form.handleReset}
              employeeName={
                form.formRef.current?.values['employee'] !== 'nobody'
                  ? `${employeeStore.currentEmployee?.general_data.first_name} ${employeeStore.currentEmployee?.general_data.last_name}`
                  : undefined
              }
              netSalaryDefault={benefitsStore.calculation?.netto_without_benefits ?? 0}
              netSalaryBenefit={benefitsStore.calculation?.netto_with_benefits ?? 0}
              grossSalary={form.formRef.current?.values['gross_salary'] * 100}
              benefitData={benefitData}
            />
            <FormStateCard
              className={state === 'fine' ? 'fadeOut' : 'fadeIn'}
              state={state}
              canCalculate
              canReset={state === 'inconsistent'}
              onResetClicked={form.handleReset}
              onSubmitClicked={form.handleSubmit}
            />
          </Box>
        </Box>
      }
      rightMinWidth="520px"
    >
      <ContentBox
        headComponent={<ContentHead headline={t('common:CALCULATOR')} showActions={false} />}
      >
        <Form
          onSubmit={(v) => {
            setState('loading');
            benefitsStore
              .calculateBenefit({
                benefit_budget: v.benefit_budget * 100,
                gross_salary: v.gross_salary * 100,
                tax_class: v.tax_class,
                birth_date: v.birth_date ? v.birth_date : '1900-01-01',
                child_allowance: v.child_allowance,
                church_tax: v.church_tax,
                betreuungskosten_amount: v.betreuungskosten_enabled
                  ? v.betreuungskosten_amount * 100
                  : 0,
                erholungsbeihilfe_amount: v.erholungsbeihilfe_enabled
                  ? v.erholungsbeihilfe_amount * 100
                  : 0,
                essenszuschuss_amount: v.essenszuschuss_enabled ? v.essenszuschuss_amount * 100 : 0,
                fahrtkosten_amount: v.fahrtkosten_enabled
                  ? Util.calcFahrtkosten(v.fahrtkosten_amount) * 100
                  : 0,
                internetpauschale_amount: v.internetpauschale_enabled
                  ? v.internetpauschale_amount * 100
                  : 0,
                öpnv_tickets_amount: v.öpnv_tickets_enabled ? v.öpnv_tickets_amount * 100 : 0,
                telefonkosten_amount: v.telefonkosten_enabled ? v.telefonkosten_amount * 100 : 0,
                sachbezug_amount: v.sachbezug_enabled ? v.sachbezug_amount * 100 : 0
              })
              .then(() => setState('fine'))
              .catch((e) => {
                console.error(e);
                setState('error');
              });
          }}
          initialValues={formPreset}
          onChange={() => setState('inconsistent')}
          formRef={form.formRef}
          useCustomButton
          enableReinitialize
        >
          <Grid item xs={12}>
            <Typography variant="subtitle1">{t('calculator:EMPLOYEE_FORM_TITLE')}</Typography>
          </Grid>
          <FormSearchableSelect
            name="employee"
            label={t('common:EMPLOYEES')}
            options={employeeList}
            xs={4}
            disableClearable
            additionalOnChange={async (id) => {
              setState('inconsistent');
              await loadPresetByUser(id as string);
              form.formRef.current?.validateField('tax_class');
            }}
          />
          <FormCurrencyField label={t('common:BUDGET')} name="benefit_budget" xs={4} />
          <FormCurrencyField label={t('common:GROSS_SALARY')} name="gross_salary" xs={4} />
          <FormTaxClassSelect
            disableClearable
            additionalOnChange={(value) => {
              const taxClass = parseInt((value as string) ?? 0);
              const childCount = parseInt(form.formRef.current?.values.child_allowance ?? 0);
              max.updateErholungsbeihilfe(taxClass, childCount);
              setState('inconsistent');
            }}
            name="tax_class"
            xs={4}
          />
          <FormChildAllowanceField
            name="child_allowance"
            additionalOnChange={(value) => {
              const childCount = parseInt((value as string) ?? 0);
              const taxClass = parseInt(form.formRef.current?.values.tax_class ?? 0);
              max.updateErholungsbeihilfe(taxClass, childCount);
              setState('inconsistent');
            }}
            xs={4}
          />
          <FormYesNoField
            additionalOnChange={() => setState('inconsistent')}
            label={t('employee:CHURCH_TAX')}
            name="church_tax"
            xs={4}
          />
          <Grid item xs={4} />
          <Grid item xs={12}>
            <Typography variant="subtitle1">{t('calculator:BENEFIT_FORM_TITLE')}</Typography>
          </Grid>
          <>
            {benefitList.map((key) => (
              <BenefitRow
                max={max.values[key.value as BenefitKey]}
                benefitKey={key.value as BenefitKey}
                key={String(key.value)}
              />
            )) ?? <></>}
          </>
          <FormTextField name="birth_date" isHidden={() => true} />
        </Form>
      </ContentBox>
    </PageWithSidebar>
  );
});
